import Modal from 'react-bootstrap/Modal';
import { useEffect, useMemo, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DataTable from '../../components/dataTable';
import PrimaryButton from '../../components/button';
import TextField from '../../components/textFiled';
import Selectfield from '../../components/selectfield';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useFetchMenuItemsQuery, useUpdateMenuItemMutation, useFetchCategoriesQuery, useUpdateCategoryMutation, useDeleteCategoryMutation } from '../../api';

type Props = {
  show: boolean;
  hide: () => void;
};

const typeItems = [
  { label: 'Snacks', value: 'Snacks' },
  { label: 'Drinks', value: 'Drinks' },
  { label: 'Other', value: 'Other' },
];

export default function ViewCategoriesModal({ show, hide }: Props) {
  const [categories, setCategories] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingType, setEditingType] = useState('Other');

  // API
  const { data: categoriesData } = useFetchCategoriesQuery('');
  const [updateCategory] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();

  // optional migration on rename
  const { data: itemsData } = useFetchMenuItemsQuery('');
  const [updateItem] = useUpdateMenuItemMutation();

  useEffect(() => {
    if (show) {
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      setEditingId(null);
      setEditingValue('');
      setEditingType('Other');
    }
  }, [show, categoriesData]);

  const headers = useMemo(() => ([
    { label: 'Sub Category Name', value: 'name' },
    { label: 'Main Category', value: 'type' },
    { label: 'Actions', value: 'actions', isAction: true },
  ]), []);

  const data = useMemo(() =>
    categories.map((c: any) => {
      const cid = c.id || c._id;
      const cname = c.name;
      const ctype = c.type || 'Other';
      return {
        id: cid,
        name: editingId === cid ? (
          <TextField
            label='Sub Category Name'
            value={editingValue}
            onChange={(e: any) => setEditingValue(e.target.value)}
            type='text'
          />
        ) : (
          <span>{cname}</span>
        ),
        type: editingId === cid ? (
          <Selectfield
            label="Main Category"
            value={editingType}
            menuItems={typeItems}
            handleChange={(e: any) => {
              setEditingType(e.target.value);
            }}
          />
        ) : (
          <span
            className={`badge bg-${ctype === 'Snacks' ? 'warning' : ctype === 'Drinks' ? 'primary' : 'secondary'}`}
            style={{
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              color: ctype === 'Snacks' ? '#000' : '#fff'
            }}
          >
            {ctype}
          </span>
        ),
        actions: (
          <div className='flex items-center gap-3'>
            {editingId === cid ? (
              <PrimaryButton
                label='Update'
                onClick={async () => {
                  const newName = editingValue.trim();
                  if (!newName) { toast.error('Name is required'); return; }
                  const prevName = categories.find((x: any) => (x.id || x._id) === cid)?.name;
                  try {
                    await updateCategory({ id: cid, body: { name: newName, type: editingType } }).unwrap();
                    setEditingId(null);
                    setEditingValue('');
                    setEditingType('Other');
                    toast.success('Category updated');
                  } catch (e: any) {
                    toast.error(e?.data?.message || 'Update failed');
                    return;
                  }
                  // migrate items (optional)
                  if (prevName && itemsData && Array.isArray(itemsData) && prevName !== newName) {
                    const items = itemsData as any[];
                    const affected = items.filter(it => it.type === prevName);
                    for (const it of affected) {
                      try {
                        await updateItem({ id: it._id, body: { ...it, type: newName } }).unwrap();
                      } catch { }
                    }
                  }
                }}
              />
            ) : (
              <EditIcon style={{ cursor: 'pointer' }} onClick={() => { setEditingId(cid); setEditingValue(cname); setEditingType(ctype); }} />
            )}
            <DeleteIcon style={{ cursor: 'pointer', color: '#FF0000' }} onClick={async () => {
              try {
                await deleteCategory(cid).unwrap();
                toast.success('Category deleted');
              } catch (e) {
                toast.error('Delete failed');
              }
            }} />
          </div>
        )
      };
    })
    , [categories, editingId, editingValue, editingType, itemsData, updateItem, updateCategory, deleteCategory]);

  return (
    <Modal show={show} onHide={hide} size="lg" centered>
      <Modal.Body>
        <div className='customer-details-title-ctn'>
          <span className='customer-details-title'>Sub Categories</span>
        </div>
        <div className='modal-details-content'>
          <DataTable headers={headers} data={data} />
          <div className='flex justify-end mt-4'>
            <button type='button' className='px-4 py-2 bg-gray-800 text-white rounded' onClick={hide}>Close</button>
          </div>
        </div>
      </Modal.Body>
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar theme='light' />
    </Modal>
  );
}
